import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

const FloatingButton = styled.div<{ $right: number; $bottom: number; $isDragging: boolean; $isSnapping: boolean }>`
  position: fixed;
  right: ${props => props.$right}px;
  bottom: ${props => props.$bottom}px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: #25D366;
  color: ${props => props.theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${props => props.theme.shadows.lg};
  z-index: ${props => props.theme.zIndex.fixed};
  cursor: ${props => props.$isDragging ? 'grabbing' : 'grab'};
  user-select: none;
  transition: ${props => {
    if (props.$isDragging) return 'none';
    if (props.$isSnapping) return 'right 0.3s ease-out, bottom 0.3s ease-out';
    return 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out, right 0.2s ease-out, bottom 0.2s ease-out';
  }};
  
  &:hover {
    transform: ${props => props.$isDragging ? 'none' : 'scale(1.1)'};
    box-shadow: ${props => props.$isDragging ? props.theme.shadows.lg : props.theme.shadows.xl};
  }
  
  &:active {
    cursor: grabbing;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    width: 50px;
    height: 50px;
  }
  
  img {
    width: 60px;
    height: 60px;
    pointer-events: none;
    object-fit: contain;
    
    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      width: 50px;
      height: 50px;
    }
  }
`;

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: ${props => props.theme.zIndex.modal};
  backdrop-filter: blur(4px);
  animation: fadeIn 0.2s ease-in-out;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  max-width: 400px;
  width: 90%;
  box-shadow: ${props => props.theme.shadows.xl};
  text-align: center;
  animation: slideUp 0.3s ease-out;
  
  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 24px;
  }
`;

const ModalIcon = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto 20px;
  background-color: #25D366;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 48px;
    height: 48px;
  }
`;

const ModalTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${props => props.theme.colors.black};
  margin: 0 0 12px 0;
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
`;

const ModalText = styled.p`
  font-size: 14px;
  color: ${props => props.theme.colors.gray[600]};
  margin: 0 0 24px 0;
  line-height: 1.5;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const ModalButton = styled.button<{ $primary?: boolean }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  
  ${props => props.$primary ? `
    background-color: #25D366;
    color: white;
    
    &:hover {
      background-color: #20BA5A;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(37, 211, 102, 0.3);
    }
  ` : `
    background-color: ${props.theme.colors.gray[100]};
    color: ${props.theme.colors.gray[700]};
    
    &:hover {
      background-color: ${props.theme.colors.gray[200]};
    }
  `}
  
  &:active {
    transform: translateY(0);
  }
`;

const WhatsAppIcon = () => (
  <img src="/Media/WA.png" alt="WhatsApp" />
);

const FloatingChatButton: React.FC = () => {
  const [position, setPosition] = useState({ right: 32, bottom: 120 }); // Higher default position (120px instead of 48px)
  const [isDragging, setIsDragging] = useState(false);
  const [isSnapping, setIsSnapping] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [startPosition, setStartPosition] = useState({ right: 0, bottom: 0 });
  const [showModal, setShowModal] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);

  // Load saved position from localStorage
  useEffect(() => {
    const savedPosition = localStorage.getItem('chatButtonPosition');
    if (savedPosition) {
      try {
        const parsed = JSON.parse(savedPosition);
        setPosition(parsed);
      } catch (e) {
        // Ignore parse errors
      }
    }
  }, []);

  // Save position to localStorage
  useEffect(() => {
    localStorage.setItem('chatButtonPosition', JSON.stringify(position));
  }, [position]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    setStartPosition({ right: position.right, bottom: position.bottom });
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({ x: touch.clientX, y: touch.clientY });
    setStartPosition({ right: position.right, bottom: position.bottom });
  };

  // Edge snapping threshold (in pixels)
  const SNAP_THRESHOLD = 20;
  
  const getButtonSize = () => {
    return window.innerWidth <= 640 ? 50 : 60; // Mobile: 50px, Desktop: 60px
  };

  const snapToEdge = (right: number, bottom: number, width: number, height: number) => {
    const buttonSize = getButtonSize();
    let snappedRight = right;
    let snappedBottom = bottom;

    // Snap to left edge (when right value is large, button is on the left)
    if (right > width - buttonSize - SNAP_THRESHOLD) {
      snappedRight = width - buttonSize;
    }
    // Snap to right edge (0 means it's at the right edge since we use right positioning)
    if (right < SNAP_THRESHOLD) {
      snappedRight = 0;
    }

    // Snap to bottom edge
    if (bottom < SNAP_THRESHOLD) {
      snappedBottom = 0;
    }
    // Snap to top edge
    if (bottom > height - buttonSize - SNAP_THRESHOLD) {
      snappedBottom = height - buttonSize;
    }

    return { right: snappedRight, bottom: snappedBottom };
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const buttonSize = getButtonSize();
      const deltaX = e.clientX - dragStart.x; // Fixed: positive when moving right
      const deltaY = e.clientY - dragStart.y; // Fixed: positive when moving down
      
      // Calculate new position (right decreases when moving right, bottom decreases when moving down)
      let newRight = Math.max(0, Math.min(window.innerWidth - buttonSize, startPosition.right - deltaX));
      let newBottom = Math.max(0, Math.min(window.innerHeight - buttonSize, startPosition.bottom - deltaY));
      
      // Apply edge snapping
      const snapped = snapToEdge(newRight, newBottom, window.innerWidth, window.innerHeight);
      
      setPosition(snapped);
    };

    const handleTouchMove = (e: TouchEvent) => {
      const buttonSize = getButtonSize();
      const touch = e.touches[0];
      const deltaX = touch.clientX - dragStart.x; // Fixed: positive when moving right
      const deltaY = touch.clientY - dragStart.y; // Fixed: positive when moving down
      
      // Calculate new position
      let newRight = Math.max(0, Math.min(window.innerWidth - buttonSize, startPosition.right - deltaX));
      let newBottom = Math.max(0, Math.min(window.innerHeight - buttonSize, startPosition.bottom - deltaY));
      
      // Apply edge snapping
      const snapped = snapToEdge(newRight, newBottom, window.innerWidth, window.innerHeight);
      
      setPosition(snapped);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      // Final snap on release with smooth transition
      const finalSnap = snapToEdge(position.right, position.bottom, window.innerWidth, window.innerHeight);
      if (finalSnap.right !== position.right || finalSnap.bottom !== position.bottom) {
        setIsSnapping(true);
        setPosition(finalSnap);
        setTimeout(() => setIsSnapping(false), 300);
      }
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
      // Final snap on release with smooth transition
      const finalSnap = snapToEdge(position.right, position.bottom, window.innerWidth, window.innerHeight);
      if (finalSnap.right !== position.right || finalSnap.bottom !== position.bottom) {
        setIsSnapping(true);
        setPosition(finalSnap);
        setTimeout(() => setIsSnapping(false), 300);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, dragStart, startPosition, position]);

  const handleClick = (e: React.MouseEvent) => {
    // Only show modal if we didn't just drag
    if (!isDragging) {
      setShowModal(true);
    }
  };

  const handleConfirm = () => {
    const phoneNumber = '201118832361'; // Egyptian number format for WhatsApp (country code + number without leading 0)
    const whatsappUrl = `https://wa.me/${phoneNumber}`;
    window.open(whatsappUrl, '_blank');
    setShowModal(false);
  };

  const handleCancel = () => {
    setShowModal(false);
  };

  return (
    <>
      <FloatingButton
        ref={buttonRef}
        $right={position.right}
        $bottom={position.bottom}
        $isDragging={isDragging}
        $isSnapping={isSnapping}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onClick={handleClick}
        aria-label="WhatsApp Contact"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            setShowModal(true);
          }
        }}
      >
        <WhatsAppIcon />
      </FloatingButton>

      <ModalOverlay $isOpen={showModal} onClick={handleCancel}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalIcon>
            <WhatsAppIcon />
          </ModalIcon>
          <ModalTitle>Connect via WhatsApp</ModalTitle>
          <ModalText>
            You will be redirected to WhatsApp to chat with our support team.
          </ModalText>
          <ModalButtons>
            <ModalButton onClick={handleCancel}>Cancel</ModalButton>
            <ModalButton $primary onClick={handleConfirm}>Continue</ModalButton>
          </ModalButtons>
        </ModalContent>
      </ModalOverlay>
    </>
  );
};

export default FloatingChatButton;
